/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any --
 * The report document is loose-typed by design (vendored prototype JS core
 * with a hand-written surface .d.ts). */

import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react"
import { ExamReportRollup } from "./core"
import { createAudioGraph } from "../pcg-viewer/audio-graph"
import {
  DEFAULT_GAIN_SLIDER_VALUE,
  formatPlaybackGain,
  playbackGainFromSlider,
} from "../pcg-viewer/audio/playback-gain"

/**
 * "Accepted report sources" chip bar — port of the prototype's
 * renderRecordings() (cardiac_report_v0_5_8.html at cardiac-panel-v4 git
 * 12d10d7). Admin-only upstream, so the page renders it in edit mode only,
 * between the recording notes and the cards. One chip per recording that
 * was accepted for Timing and/or Structural Sound, saying what it
 * contributes, with the exam roll-up rule behind the ⓘ tooltip. Rejected,
 * unreviewed and non-contributing recordings stay in the review workspace
 * and are not listed (they still appear, as text, in the summary below).
 *
 * Chip click plays the recording inline. The prototype's chips are static —
 * its play control only ever showed a notice, so it was removed; here the
 * control plays the accepted recording, so it stays. One hidden <audio>
 * element serves every chip, routed through the
 * viewer's Web Audio graph so the same 0–5× listening gain applies; the
 * blob for each recording is fetched once through `loadAudio` and kept for
 * the life of the bar. Without `loadAudio` the bar falls back to the
 * prototype's notice through `onNotice`.
 */

const ROLLUP_RULE =
  "Each accepted recording contributes the evidence it was accepted for: Timing, Structural Sound, or both. " +
  "Heart Rate, SYS:DIA Ratio, and SYS:DIA Duration preserve the range across the recordings included by the " +
  "reviewer; the card marker is a representative median only. Recordings whose measured (uncorrected) rates do " +
  "not sit within ±10% of the others, such as a suspected half or double count, are noted and never corrected or " +
  "removed; Cycle Regularity flags if any accepted source is irregular; Structural Sound uses the most " +
  "significant reportable finding among the Structural Sound recordings, with its position; Signal Quality is " +
  "the lowest among the recordings that contribute."

const SINGLE_RECORDING_RULE =
  "Single-recording scope: findings apply to this recording only. This does not reduce confidence in strong " +
  "recording-level findings, but it does not rule out findings at other positions."

const PLAYBACK_NOTICE =
  "Playback opens the linked PCG recording in the review workspace with reviewer context preserved. " +
  "Inline playback from the Medaica Report is not available yet."

const PLAYBACK_FAILED_NOTICE = "The recording audio could not be loaded."

type LoadAudio = (recordingId: string) => Promise<Blob>

type PlaybackStatus = "idle" | "loading" | "playing" | "paused"

/**
 * Inline playback state for the chip bar: which recording is loaded, whether
 * it is playing, and the listening gain. Owns the one <audio> element's Web
 * Audio graph (created once per mount, closed on unmount — the media-element
 * source capture is one-shot) and the object URLs of the fetched blobs.
 */
const useExamSourcePlayback = (
  loadAudio: LoadAudio | undefined,
  onLoadFailed: (recordingId: string) => void
): {
  audioRef: React.RefObject<HTMLAudioElement>
  activeId: string | null
  status: PlaybackStatus
  gainSlider: number
  setGainSlider: (value: number) => void
  toggle: (recordingId: string) => void
} => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const graphRef = useRef<any>(null)
  const urlsRef = useRef(new Map<string, Promise<string>>())
  const requestRef = useRef(0)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [status, setStatus] = useState<PlaybackStatus>("idle")
  const [gainSlider, setGainSliderState] = useState<number>(DEFAULT_GAIN_SLIDER_VALUE)
  const stateRef = useRef({ activeId, status, gainSlider })
  stateRef.current = { activeId, status, gainSlider }

  const applyGain = useCallback((sliderValue: number) => {
    const gain = playbackGainFromSlider(sliderValue)
    if (graphRef.current) {
      graphRef.current.setAmplification(gain)
    } else if (audioRef.current) {
      // No Web Audio: the element's own volume is the best available control.
      audioRef.current.volume = Math.min(1, gain)
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) {
      return undefined
    }
    const graph = createAudioGraph(audio)
    graphRef.current = graph
    applyGain(stateRef.current.gainSlider)
    const onPlay = (): void => {
      // A context created before any user gesture starts suspended.
      if (graph) graph.resume()
      setStatus("playing")
    }
    // Swapping `src` on a playing element pauses it; that pause belongs to
    // the load in progress, not to the user.
    const onPause = (): void => setStatus((current) => (current === "loading" ? current : "paused"))
    const onEnded = (): void => setStatus("idle")
    audio.addEventListener("play", onPlay)
    audio.addEventListener("pause", onPause)
    audio.addEventListener("ended", onEnded)
    const urls = urlsRef.current
    return () => {
      audio.removeEventListener("play", onPlay)
      audio.removeEventListener("pause", onPause)
      audio.removeEventListener("ended", onEnded)
      if (graph) graph.close()
      graphRef.current = null
      urls.forEach((pending) => {
        pending.then((url) => URL.revokeObjectURL(url)).catch(() => undefined)
      })
      urls.clear()
    }
  }, [applyGain])

  const setGainSlider = useCallback(
    (value: number) => {
      setGainSliderState(value)
      applyGain(value)
    },
    [applyGain]
  )

  const urlFor = useCallback(
    (recordingId: string): Promise<string> => {
      const cached = urlsRef.current.get(recordingId)
      if (cached) {
        return cached
      }
      if (!loadAudio) {
        return Promise.reject(new Error("no audio loader"))
      }
      const pending = loadAudio(recordingId).then((blob) => URL.createObjectURL(blob))
      pending.catch(() => urlsRef.current.delete(recordingId))
      urlsRef.current.set(recordingId, pending)
      return pending
    },
    [loadAudio]
  )

  const play = useCallback(async (): Promise<void> => {
    const audio = audioRef.current
    if (!audio) {
      return
    }
    if (graphRef.current) graphRef.current.resume()
    await audio.play()
  }, [])

  const toggle = useCallback(
    (recordingId: string) => {
      const audio = audioRef.current
      if (!audio) {
        return
      }
      const current = stateRef.current
      if (recordingId === current.activeId) {
        if (current.status === "playing") {
          audio.pause()
        } else if (current.status !== "loading") {
          play().catch(() => onLoadFailed(recordingId))
        }
        return
      }
      const token = ++requestRef.current
      setActiveId(recordingId)
      setStatus("loading")
      urlFor(recordingId)
        .then(async (url) => {
          if (token !== requestRef.current) {
            return
          }
          audio.src = url
          await play()
        })
        .catch(() => {
          if (token === requestRef.current) {
            setStatus("idle")
            onLoadFailed(recordingId)
          }
        })
    },
    [onLoadFailed, play, urlFor]
  )

  return { audioRef, activeId, status, gainSlider, setGainSlider, toggle }
}

const isSingleRecordingScope = (recording: any): boolean =>
  recording.examScope === "single_recording" || recording.coverageStatus === "single_recording_scope"

/** One-line account of the exam roll-up: which recordings fed the summary and the review flag, if any. */
const rollupSummary = (rollup: ExamReportRollup | null): { lines: string[]; reviewReason: string | null } | null => {
  if (!rollup || !rollup.timing) {
    return null
  }
  const positions = new Map<string, string | null>(
    (rollup.recordings || []).map((r): [string, string | null] => [r.recordingId, r.position])
  )
  const label = (id: string): string => positions.get(id) || id
  const included: string[] = rollup.timing.includedRecordingIds || []
  // Earlier roll-ups left recordings out of the heart rate; current ones
  // keep every accepted recording and name the ones outside the agreement
  const excluded: string[] = rollup.timing.excludedRecordingIds || []
  const discrepant: string[] = rollup.timing.discrepantRecordingIds || []
  const lines: string[] = []
  const leftOut = excluded.length ? ` · left out: ${excluded.map(label).join(", ")}` : ""
  const outsideAgreement = discrepant.length ? ` · outside the ±10% agreement: ${discrepant.map(label).join(", ")}` : ""
  lines.push(
    included.length
      ? `Heart rate ${rollup.timing.observed} from ${included.map(label).join(", ")}${leftOut}${outsideAgreement}`
      : rollup.timing.noTimingAccepted
      ? "Heart rate: no recording was accepted for Timing"
      : "Heart rate: not assessed from the contributing recordings"
  )
  lines.push(
    rollup.structuralSound?.sourceRecordingId
      ? `Structural Sound from ${label(rollup.structuralSound.sourceRecordingId)}`
      : rollup.structuralSound?.noStructuralAccepted
      ? "Structural Sound: no recording was accepted for Structural Sound"
      : "Structural Sound: no contributing recording was assessable"
  )
  // Which evidence each accepted recording was accepted for — shown once any
  // of them is not accepted for both
  const accepted = (rollup.recordings || []).filter((r) => r.includedForExamRollup && r.acceptedDomains)
  if (accepted.some((r) => !(r.acceptedDomains?.timing && r.acceptedDomains?.structuralSound))) {
    lines.push(
      `Accepted evidence: ${accepted
        .map(
          (r) =>
            `${label(r.recordingId)} (${[
              r.acceptedDomains?.timing ? "Timing" : "",
              r.acceptedDomains?.structuralSound ? "Structural Sound" : "",
            ]
              .filter(Boolean)
              .join(" + ")})`
        )
        .join(", ")}`
    )
  }
  const notContributing = rollup.excludedByReviewStatus || []
  if (notContributing.length) {
    lines.push(
      `Not contributing: ${notContributing
        .map((r) => `${label(r.recordingId)} (${r.reviewStatus === "rejected" ? "rejected" : "not reviewed"})`)
        .join(", ")}`
    )
  }
  return { lines, reviewReason: rollup.timing.reviewRequired ? rollup.timing.reviewReason || null : null }
}

const HeartReportExamSource = ({
  recordings,
  examRollup = null,
  onNotice,
  loadAudio,
}: {
  recordings: any[]
  examRollup?: ExamReportRollup | null
  onNotice: (title: string, message: string) => void
  /** Fetches a recording's audio by its recording id; omitted = notice-only chips. */
  loadAudio?: LoadAudio
}): ReactElement | null => {
  const positionOf = (recording: any): string =>
    String(recording.position || recording.recordingName || recording.name || recording.sourceName || "Unlabeled")
  const idOf = (recording: any, index: number): string =>
    String(recording.recordingId || recording.id || recording.url || recording.recordingName || recording.name || index)
  const onLoadFailed = useCallback(
    (recordingId: string) => {
      const index = (recordings || []).findIndex((recording: any, i: number) => idOf(recording, i) === recordingId)
      const position = index >= 0 ? positionOf(recordings[index]) : "recording"
      onNotice(`Play ${position}`, PLAYBACK_FAILED_NOTICE)
    },
    [onNotice, recordings]
  )
  const playback = useExamSourcePlayback(loadAudio, onLoadFailed)
  // What each recording was accepted for: on the chip itself, or — for a
  // document saved before chips carried it — in the roll-up's audit block
  const rollupById = new Map((examRollup?.recordings || []).map((r: any) => [String(r.recordingId), r]))
  const contributionOf = (recording: any, index: number): { known: boolean; timing: boolean; structural: boolean } => {
    const audit: any = rollupById.get(idOf(recording, index))
    const domains = recording.acceptedDomains || audit?.acceptedDomains || null
    const reviewStatus = recording.reviewStatus || audit?.reviewStatus || null
    // The roll-up records no domains for a recording that was not accepted;
    // a stated review status other than accepted rules the recording out
    const accepted = !reviewStatus || reviewStatus === "accepted"
    return {
      known: Boolean(domains || reviewStatus),
      timing: accepted && Boolean(domains?.timing),
      structural: accepted && Boolean(domains?.structuralSound),
    }
  }
  const contributions = (recordings || []).map((recording: any, index: number) => contributionOf(recording, index))
  // Documents from before domain acceptance say nothing: list every recording
  const hasExplicitAcceptance = contributions.some((entry) => entry.known)
  const sources = (recordings || [])
    .map((recording: any, index: number) => ({ recording, index, contribution: contributions[index] }))
    .filter(({ contribution }) => !hasExplicitAcceptance || contribution.timing || contribution.structural)
  if (sources.length === 0) {
    return null
  }
  const summary = rollupSummary(examRollup)
  const hasSingleRecordingScope = recordings.length === 1 && recordings.some(isSingleRecordingScope)
  const rule = hasSingleRecordingScope ? SINGLE_RECORDING_RULE : ROLLUP_RULE
  return (
    <div className="recordings-bar" data-testid="exam-source">
      <div className="rec-label">
        <span>Accepted report sources</span>
        <button type="button" className="title-help" data-help={rule} aria-label="Accepted report sources help">
          i
        </button>
        {loadAudio ? (
          <label className="rec-gain" title="Listening gain — playback only, never applied to analysis">
            <span className="rec-gain-label" aria-live="polite">
              Gain {formatPlaybackGain(playbackGainFromSlider(playback.gainSlider))}
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={playback.gainSlider}
              aria-label="Playback amplification"
              onChange={(event) => playback.setGainSlider(Number(event.target.value))}
            />
          </label>
        ) : null}
      </div>
      <div className="rec-list">
        {sources.map(({ recording, index, contribution }) => {
          const position = positionOf(recording)
          const contributes = [contribution.timing ? "Timing" : "", contribution.structural ? "Structural" : ""]
            .filter(Boolean)
            .join(" + ")
          const duration = recording.duration ? String(recording.duration) : ""
          const scopeLabel = isSingleRecordingScope(recording) ? "single recording" : ""
          const meta = [recording.deviceId, recording.recordedAt, duration, scopeLabel, recording.coverageNote]
            .filter(Boolean)
            .join(" · ")
          const id = idOf(recording, index)
          const active = loadAudio ? playback.activeId === id : false
          const playing = active && playback.status === "playing"
          const loading = active && playback.status === "loading"
          const classes = ["rec-play", active ? "active" : "", playing ? "playing" : ""].filter(Boolean).join(" ")
          return (
            <button
              type="button"
              key={id}
              className={classes}
              title={meta || "Accepted report source"}
              aria-label={`${playing ? "Pause" : "Play"} ${position}`}
              aria-pressed={loadAudio ? playing : undefined}
              aria-busy={loading || undefined}
              onClick={() => (loadAudio ? playback.toggle(id) : onNotice(`Play ${position}`, PLAYBACK_NOTICE))}
            >
              <span className="rec-icon" aria-hidden="true">
                {playing ? "❚❚" : "▶"}
              </span>
              <span className="rec-position">{position}</span>
              {contributes ? <span className="rec-duration">— {contributes}</span> : null}
              {duration ? <span className="rec-duration">{duration}</span> : null}
            </button>
          )
        })}
      </div>
      {loadAudio ? <audio ref={playback.audioRef} preload="auto" data-testid="exam-source-audio" /> : null}
      {summary ? (
        <div className="rec-rollup" data-testid="exam-rollup-summary">
          {summary.lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
          {summary.reviewReason ? (
            <div className="phr-review-flag" role="note">
              Note — {summary.reviewReason}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default HeartReportExamSource
